import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (data) => api.post("/auth/login", data),
  register: (data) => api.post("/auth/register", data),
  me: () => api.get("/auth/me"),
};

// User APIs
export const userAPI = {
  list: () => api.get("/users"),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

// Client APIs
export const clientAPI = {
  list: (search = "") => api.get(`/clients${search ? `?search=${search}` : ""}`),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post("/clients", data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Product APIs
export const productAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append("search", params.search);
    if (params.is_service !== undefined) queryParams.append("is_service", params.is_service);
    if (params.category) queryParams.append("category", params.category);
    return api.get(`/products?${queryParams.toString()}`);
  },
  get: (id) => api.get(`/products/${id}`),
  create: (data) => api.post("/products", data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getCategories: () => api.get("/products/categories"),
};

// Invoice APIs
export const invoiceAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/invoices?${queryParams.toString()}`);
  },
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post("/invoices", data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  getPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: "blob" }),
  getNextNumber: (invoiceType = "tax_invoice") => api.get(`/invoices/next-number?invoice_type=${invoiceType}`),
  checkNumber: (number, invoiceType = "tax_invoice") => api.get(`/invoices/check-number/${encodeURIComponent(number)}?invoice_type=${invoiceType}`),
};

// Payment APIs
export const paymentAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/payments?${queryParams.toString()}`);
  },
  create: (data) => api.post("/payments", data),
  delete: (id) => api.delete(`/payments/${id}`),
};

// Report APIs
export const reportAPI = {
  clientLedger: (clientId) => api.get(`/reports/client-ledger/${clientId}`),
  ageing: () => api.get("/reports/ageing"),
  gstSummary: (fromDate, toDate) => api.get(`/reports/gst-summary?from_date=${fromDate}&to_date=${toDate}`),
  employeeReimbursements: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/reports/employee-reimbursements?${queryParams.toString()}`);
  },
  expenseSummary: () => api.get("/reports/expense-summary"),
};

// Dashboard APIs
export const dashboardAPI = {
  stats: () => api.get("/dashboard/stats"),
};

// Settings APIs
export const settingsAPI = {
  get: () => api.get("/settings"),
  update: (data) => api.put("/settings", data),
};

// Audit Log APIs
export const auditAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/audit-logs?${queryParams.toString()}`);
  },
};

// Project APIs
export const projectAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/projects?${queryParams.toString()}`);
  },
  get: (id) => api.get(`/projects/${id}`),
  getProfitability: (id) => api.get(`/projects/${id}/profitability`),
  create: (data) => api.post("/projects", data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

// Vendor APIs
export const vendorAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/vendors?${queryParams.toString()}`);
  },
  get: (id) => api.get(`/vendors/${id}`),
  getLedger: (id) => api.get(`/vendors/${id}/ledger`),
  create: (data) => api.post("/vendors", data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
};

// Vendor Bill APIs
export const vendorBillAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/vendor-bills?${queryParams.toString()}`);
  },
  get: (id) => api.get(`/vendor-bills/${id}`),
  create: (data) => api.post("/vendor-bills", data),
  update: (id, data) => api.put(`/vendor-bills/${id}`, data),
  delete: (id) => api.delete(`/vendor-bills/${id}`),
};

// Vendor Payment APIs
export const vendorPaymentAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/vendor-payments?${queryParams.toString()}`);
  },
  create: (data) => api.post("/vendor-payments", data),
  delete: (id) => api.delete(`/vendor-payments/${id}`),
};

// Expense APIs
export const expenseAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/expenses?${queryParams.toString()}`);
  },
  get: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post("/expenses", data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
};

// Quotation APIs
export const quotationAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/quotations?${queryParams.toString()}`);
  },
  get: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post("/quotations", data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  convert: (id, data) => api.post(`/quotations/${id}/convert`, data),
};

// Credit/Debit Note APIs
export const creditDebitNoteAPI = {
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        queryParams.append(key, value);
      }
    });
    return api.get(`/credit-debit-notes?${queryParams.toString()}`);
  },
  get: (id) => api.get(`/credit-debit-notes/${id}`),
  create: (data) => api.post("/credit-debit-notes", data),
  delete: (id) => api.delete(`/credit-debit-notes/${id}`),
};

// Search API
export const searchAPI = {
  global: (query) => api.get(`/search/global?q=${encodeURIComponent(query)}`),
};

export default api;
